import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, Provider } from '@angular/core';
import { PortalApiInterceptor } from '@common/interceptors/auth-key.interceptor';
import { PublicAdvancedSearchSandboxService } from '@common/sandboxes/public-advanced-search-sandbox.service';
import { PortalZoneRestrictions } from '@common/services/api/helpers/portal-zone-restrictions.class';
import { PortalConfigService } from '@common/services/config/portal-config.service';
import { PortalStoreRecordsStateService } from '@common/services/search-context/store-record-state.service';
import { LAZY_MAPS_API_CONFIG } from '@duxor/agm-core';
import { APP_NAME_TOKEN, AppName, LocaleProvider } from '@language';
import {
    DeferredMessageFormatCompiler,
    RefreshShareTokenStrategy,
    RefreshTokenInterceptor,
    SearchService
} from '@mediahaven/shared';
import {
    AbstractRecordsStateService,
    ApplicationConfigService,
    BaseAdvancedSearchSandbox,
    CACHE_CONFIG,
    ConfigService,
    EnvironmentConfigService,
    GoogleMapsConfig,
    PAGE_TITLE_CONFIG,
    ShCacheInterceptor,
    ShRecordService,
    ZoneRestrictions
} from '@stylehaven/components';
import { BootstrapService } from '../bootstrap.service';

export const DEFERRED_COMPILER: DeferredMessageFormatCompiler = new DeferredMessageFormatCompiler();

export const BOOTSTRAP_PROVIDERS: Provider[] = [
    // #region Translations and Locales
    {
        provide: APP_NAME_TOKEN,
        useValue: 'portal' as AppName
    },
    {
        provide: APP_INITIALIZER,
        useFactory: (bootstrapService: BootstrapService) => () => bootstrapService.initializeApp(),
        deps: [BootstrapService],
        multi: true
    },
    {
        provide: PAGE_TITLE_CONFIG,
        useValue: {
            usePageHeaderSuffix: false
        }
    },
    {
        provide: DeferredMessageFormatCompiler,
        useValue: DEFERRED_COMPILER
    },
    LocaleProvider,
    // #endregion
    // #region Config
    {
        provide: EnvironmentConfigService,
        useExisting: PortalConfigService
    },
    {
        provide: LAZY_MAPS_API_CONFIG,
        useClass: GoogleMapsConfig,
        deps: [PortalConfigService]
    },
    {
        provide: ConfigService,
        useExisting: ApplicationConfigService
    },

    // #endregion
    // #region Interceptors
    {
        provide: HTTP_INTERCEPTORS,
        useClass: ShCacheInterceptor,
        multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: RefreshTokenInterceptor, multi: true },
    { provide: 'REFRESH_STRATEGIES', useClass: RefreshShareTokenStrategy, multi: true },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: PortalApiInterceptor,
        multi: true
    },
    // #endregion
    // # region Core services
    {
        provide: AbstractRecordsStateService,
        useClass: PortalStoreRecordsStateService
    },
    {
        provide: ShRecordService,
        useClass: SearchService
    },
    {
        provide: CACHE_CONFIG,
        useValue: [
            { url: /profiles(\/|\?)/ },
            { url: /organisations(?:\/|\?|$)/ },
            { url: /classifications(?:\/|\?|$)/ },
            { url: /shares(?:\/|\?|$)/ },
            { url: /export-locations\/default(?:\/|\?|$)/ }
        ]
    },
    {
        provide: ZoneRestrictions,
        useClass: PortalZoneRestrictions
    },
    {
        provide: BaseAdvancedSearchSandbox,
        useExisting: PublicAdvancedSearchSandboxService
    }
    // #endregion
];
